import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ClockCounterClockwise, CheckCircle, Users, CurrencyEur, TrendUp, Wallet, CalendarBlank, ListChecks, House, Bell, Headset, Ticket, Briefcase, ShieldCheck } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OperationsFeed from "@/components/OperationsFeed";
import DashboardCustomerSearch from "@/components/DashboardCustomerSearch";

function StatCard({ label, value, icon: Icon, tone = "default", testId, sub }) {
  const toneClasses = {
    default: "text-foreground",
    warn: "text-amber-600 dark:text-amber-400",
    ok: "text-emerald-600 dark:text-emerald-400",
    primary: "text-primary",
    danger: "text-rose-600 dark:text-rose-400",
  };
  return (
    <Card data-testid={testId} className="p-5 border-border/70 rounded-lg hover:shadow-md hover:-translate-y-0.5"
          style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
          <div className={`mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight ${toneClasses[tone]}`}>{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1 font-mono">{sub}</div>}
        </div>
        <Icon size={22} weight="regular" className="text-muted-foreground shrink-0" />
      </div>
    </Card>
  );
}

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const EXPIRING_WINDOW_DAYS = 30;

export default function DashboardPage() {
  useDocumentTitle("Dashboard");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState({ open: 0, closed: 0, customers: 0, pending_tasks: 0, pending_works: 0, expiring_services: 0, expiring_notice_days: EXPIRING_WINDOW_DAYS });
  const [ticketCount, setTicketCount] = useState({ new: 0, total: 0 });
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [availableYears, setAvailableYears] = useState([currentYear]);
  const [revenue, setRevenue] = useState({ months: [], year_revenue: 0, year_cost: 0, year_profit: 0, year_count: 0, year: currentYear });
  const [servicesRevenue, setServicesRevenue] = useState({ revenue: 0, count: 0 });
  const [showCosts, setShowCosts] = useState(false);

  const load = async () => {
    const [s, r, yrs, sr, tc] = await Promise.all([
      api.get("/stats", { params: { expiring_within: EXPIRING_WINDOW_DAYS } }),
      api.get("/stats/revenue", { params: { year: selectedYear } }),
      api.get("/stats/revenue/years"),
      api.get("/stats/services", { params: { year: selectedYear } }),
      api.get("/tickets/count"),
    ]);
    setStats(s.data);
    setRevenue(r.data);
    setAvailableYears(yrs.data.years);
    setServicesRevenue(sr.data);
    setTicketCount(tc.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [selectedYear]);

  // Auto-refresh tickets count every 45s
  useEffect(() => {
    const t = setInterval(() => {
      api.get("/tickets/count").then(({ data }) => setTicketCount(data)).catch(() => {});
    }, 45000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// PANNELLO</div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Panoramica generale: KPI, andamento economico e movimenti registrati.</p>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <DashboardCustomerSearch />
          {/* Small clickable customers pill */}
          <Link to="/clienti" data-testid="header-customers-pill" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 border border-border/60" style={{ transition: "background-color 0.15s ease" }}>
            <Users size={14} className="text-primary" weight="duotone" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Clienti</span>
            <span className="font-display font-bold text-base leading-none">{stats.customers}</span>
          </Link>
        </div>
      </header>

      {ticketCount.new > 0 && (
        <div className="mb-4" data-testid="tickets-alert-banner">
          <Link to="/ticket" className="block">
            <Card className="p-4 border-primary/40 bg-primary/5 rounded-lg hover:bg-primary/10" style={{ transition: "background-color 0.15s ease" }}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center relative">
                  <Ticket size={20} weight="duotone" />
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold font-mono">{ticketCount.new}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold tracking-tight">
                    {ticketCount.new === 1 ? "1 nuova richiesta di assistenza" : `${ticketCount.new} nuove richieste di assistenza`}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">
                    Ricevute dal form pubblico. Clicca per gestirle. Aggiornamento automatico ogni 45s.
                  </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-primary shrink-0">Apri →</div>
              </div>
            </Card>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Link to="/interventi-laboratorio" className="block">
          <StatCard testId="stat-open" label="Interventi aperti" value={stats.open} icon={ClockCounterClockwise} tone="warn" />
        </Link>
        <Link to="/attivita?tab=tasks" className="block">
          <StatCard testId="stat-pending-tasks" label="Task da fare" value={stats.pending_tasks} icon={ListChecks} tone={stats.pending_tasks > 0 ? "primary" : "default"} />
        </Link>
        <Link to="/attivita?tab=works" className="block">
          <StatCard testId="stat-pending-works" label="Lavori aperti" value={stats.pending_works || 0} icon={Briefcase} tone={(stats.pending_works || 0) > 0 ? "primary" : "default"} />
        </Link>
        <Link to="/scadenzario" data-testid="stat-expiring-services-link" className="block">
          <StatCard
            testId="stat-expiring-services"
            label={`Scadenze prossimi ${stats.expiring_notice_days || EXPIRING_WINDOW_DAYS}gg`}
            value={stats.expiring_services}
            icon={Bell}
            tone={stats.expiring_services > 0 ? "danger" : "default"}
          />
        </Link>
      </div>

      {/* Year summary — admin only */}
      {isAdmin && (
      <Card className="p-5 border-border/70 rounded-lg mb-6" data-testid="year-summary">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// RIEPILOGO {revenue.year}</div>
              <div className="font-display text-lg font-bold tracking-tight mt-1">
                Attività dell'anno · si azzera automaticamente il 1° gennaio
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CalendarBlank size={16} className="text-muted-foreground" />
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v, 10))}>
                <SelectTrigger data-testid="year-selector" className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableYears.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`year-option-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Mostra dettagli</span>
              <Switch checked={showCosts} onCheckedChange={setShowCosts} data-testid="show-costs-toggle" />
            </label>
          </div>
        </div>

        {/* Vista base: 5 card fatturato (Officina, Domicilio, Servizi, Assistenze, Lavori) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            testId="stat-workshop-revenue"
            label="Fatturato officina"
            value={eur(revenue.workshop_revenue || 0)}
            icon={ClockCounterClockwise}
            tone="primary"
            sub={`${revenue.workshop_count || 0} interventi chiusi`}
          />
          <StatCard
            testId="stat-home-revenue"
            label="Fatturato domicilio"
            value={eur(revenue.home_revenue || 0)}
            icon={House}
            tone="primary"
            sub={`${revenue.home_count || 0} interventi`}
          />
          <StatCard
            testId="stat-services-revenue"
            label="Fatturato servizi"
            value={eur(servicesRevenue.services_revenue || 0)}
            icon={ShieldCheck}
            tone="primary"
            sub={`${servicesRevenue.services_count || 0} rinnovi/vendite`}
          />
          <StatCard
            testId="stat-assistance-revenue"
            label="Fatturato assistenze"
            value={eur(servicesRevenue.assistance_revenue || 0)}
            icon={Headset}
            tone="primary"
            sub={`${servicesRevenue.assistance_count || 0} assistenze/ticket`}
          />
          <StatCard
            testId="stat-works-revenue"
            label="Fatturato lavori"
            value={eur(servicesRevenue.works_revenue || 0)}
            icon={Briefcase}
            tone="primary"
            sub={`${servicesRevenue.works_count || 0} lavori chiusi`}
          />
        </div>

        {/* Dettagli mostrabili on-demand */}
        {showCosts && (
          <div className="mt-5 pt-5 border-t border-border/50 space-y-5" data-testid="revenue-details">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                // Dettaglio Interventi
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard testId="stat-year-count" label={`Interventi chiusi ${revenue.year}`} value={revenue.year_count} icon={CheckCircle} tone="ok" />
                <StatCard testId="stat-year-revenue" label="Fatturato interventi" value={eur(revenue.year_revenue)} icon={CurrencyEur} tone="primary" />
                <StatCard testId="stat-year-cost" label="Costi sostenuti" value={eur(revenue.year_cost)} icon={Wallet} tone="danger" />
                <StatCard
                  testId="stat-year-profit"
                  label="Guadagno interventi"
                  value={eur(revenue.year_profit)}
                  icon={TrendUp}
                  tone={revenue.year_profit >= 0 ? "ok" : "danger"}
                  sub={revenue.year_revenue > 0 ? `Margine ${((revenue.year_profit / revenue.year_revenue) * 100).toFixed(1)}%` : null}
                />
              </div>
            </div>

            {(revenue.home_count > 0 || revenue.workshop_count > 0) && (
              <div data-testid="revenue-breakdown">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                  // Suddivisione per tipologia
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg border border-border/60 bg-secondary/30" data-testid="breakdown-workshop">
                    <div className="flex items-center gap-2 mb-2">
                      <ClockCounterClockwise size={16} className="text-primary" />
                      <span className="font-mono text-[10px] uppercase tracking-widest">Officina</span>
                    </div>
                    <div className="text-2xl font-display font-bold">{eur(revenue.workshop_revenue)}</div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      Costi {eur(revenue.workshop_cost)} · Guadagno {eur(revenue.workshop_profit)} · {revenue.workshop_count} interventi
                    </div>
                  </div>
                  <div className="p-4 rounded-lg border border-border/60 bg-secondary/30" data-testid="breakdown-home">
                    <div className="flex items-center gap-2 mb-2">
                      <House size={16} className="text-primary" weight="fill" />
                      <span className="font-mono text-[10px] uppercase tracking-widest">Domicilio</span>
                    </div>
                    <div className="text-2xl font-display font-bold">{eur(revenue.home_revenue)}</div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      Costi {eur(revenue.home_cost)} · Guadagno {eur(revenue.home_profit)} · {revenue.home_count} interventi
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                // Situazione Scadenzario
              </div>
              <Link to="/scadenzario" data-testid="services-status-link" className="block">
                <StatCard
                  testId="stat-services-status"
                  label="Situazione scadenzario"
                  value={`${servicesRevenue.active_count || 0} attivi`}
                  icon={Bell}
                  tone={((servicesRevenue.expiring_count || 0) + (servicesRevenue.expired_count || 0)) > 0 ? "danger" : "ok"}
                  sub={`${servicesRevenue.expiring_count || 0} in scadenza · ${servicesRevenue.expired_count || 0} scaduti`}
                />
              </Link>
            </div>
          </div>
        )}
      </Card>
      )}

      <OperationsFeed year={selectedYear} />
    </div>
  );
}
